import HighchartsReact from "highcharts-react-official";
import { groupBy } from "lodash";
import * as React from "react";
import * as Highcharts from "highcharts/highstock";
import { PaymentPayerMapper, PaymentTypeMapper } from "../../Constant/Content";
import { SubAccountType } from "../../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { JsonDateParser } from "../../../../../../../../../react-core/Utils/JsonDateParser";
import { PaymentPayer } from "../../../../../../../../../Wrap/Constants/Enums/Payments/PaymentPayer";
import AriaTooltip from "../../../../../../../../Shared/Tooltip/AriaTooltip";
import { MoneyDisplayHelper } from "../../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Table } from "../../../../../../../../Shared/Table/Table";
import { addMonths, addYears } from "../../../../../../../../../react-core/utils/date";
import { ProductOverviewPaymentsInAction } from "../../Action/ProductOverviewPaymentsInAction";
import { SelectEditor } from "../../../../../../../../../react-core/Html/SelectEditor";

interface ICompletedInstructionsProps {
    subAccountHierarchyId: string;
    selectedProduct: SubAccountType;
}

const options = (data: any): Highcharts.Options => {
    return {
        chart: {
            renderTo: "container",
            type: "column",
            style: {
                fontFamily: "inherit",
            },
            height: 250,
            backgroundColor: "transparent",
            className: "instructions-column-chart-isa",
        },
        title: {
            text: "",
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            crosshair: true,
            lineColor: "transparent",
            tickWidth: 0,
            tickLength: 0,
            tickColor: "#979797",
            type: "datetime",
            showFirstLabel: true,
            showLastLabel: true,
            gridLineWidth: 0,
            labels: {
                useHTML: true,
                style: {
                    color: "#0a2f73",
                },
                align: "center",
                enabled: true,
                step: 1,
                formatter: function () {
                    const date = new Date(this.value);
                    const month = JsonDateParser.getMonthNameMapping((date.getMonth() + 1).toString(), true);
                    const year = JsonDateParser.getFormatDate(JsonDateParser.toJsonDateNoOffset(date), "yy");

                    const formatDate = month + " " + year;
                    return formatDate;
                },
            },
            title: {
                text: "",
            },
            minRange: 24 * 3600 * 1000 * 365,
        },
        yAxis: {
            maxPadding: 0,
            allowDecimals: false,
            gridLineWidth: 0,
            title: null,
            labels: {
                formatter: function () {
                    return "£" + this.value;
                },
                style: {
                    color: "#0a2f73",
                },
                step: 1,
                useHTML: true,
            },
        },
        tooltip: {
            formatter: function () {
                return "<b>" + this.series.name + "</b>: £" + this.y;
            },
            borderColor: "transparent",
            useHTML: true,
        },
        legend: {
            symbolHeight: 14,
            symbolWidth: 14,
            symbolRadius: 0,
            symbolPadding: 10,
            useHTML: true,
            itemStyle: { lineHeight: "14px" },
        },
        plotOptions: {
            column: {
                stacking: "normal",
                dataLabels: {
                    enabled: false,
                },
                states: {
                    inactive: {
                        opacity: 1,
                    },
                },
            },
        },
        series: composeDataSeries(data),
    };
};

const renderChartAndTable = (data: any, props) => {
    const tableData = composeTableData(data);
    let singleTotal = 0,
        regularTotal = 0;
    tableData.forEach((element) => {
        singleTotal += element["Single"] || 0;
        regularTotal += element["Regular"] || 0;
    });

    const cols = [
        {
            title: "Payer",
            key: "Payer",
            thInnerCss: "sr-only",
            render: (data) => {
                if (data === null || data === "" || data === PaymentPayer.NotSpecified) {
                    return (
                        <AriaTooltip
                            cssWrapper="is-underlined-text no-border-radius"
                            buttonText={"No recorded Payee"}
                            showCloseButton={true}
                        >
                            We don’t have a record of the payee for this transaction
                        </AriaTooltip>
                    );
                } else {
                    return PaymentPayerMapper[data];
                }
            },
        },
        {
            title: "Single payment",
            key: "Single",
            thCssWrapper: "cell-right",
            tdCssWrapper: "cell-right",
            render: (data) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
            },
        },
    ];

    const totalBody = [
        { data: "Total", colSpan: 1 },
        {
            data: MoneyDisplayHelper.FromatMoneyDisplay(singleTotal, CurrencyCode.GBP, true, 2),
            colSpan: 1,
            tfTdCssWrapper: "cell-right",
        },
    ];
    if (props.SelectedProduct !== SubAccountType.OnshoreBond && props.SelectedProduct !== SubAccountType.OffshoreBond) {
        // Add regular payment
        cols.push({
            title: "Regular payment",
            key: "Regular",
            thCssWrapper: "cell-right",
            tdCssWrapper: "cell-right",
            render: (data) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(data || 0, CurrencyCode.GBP, true, 2);
            },
        });
        totalBody.push({
            data: MoneyDisplayHelper.FromatMoneyDisplay(regularTotal, CurrencyCode.GBP, true, 2),
            colSpan: 1,
            tfTdCssWrapper: "cell-right",
        });
    }
    return (
        <>
            <div className="panel-subtle mb-35">
                <HighchartsReact highcharts={Highcharts} options={options(data)} allowChartUpdate={true} />
            </div>
            <div className="panel-subtle pt-10 pb-10 mb-40">
                <Table
                    cssWrapper="table-subtle table-first-last-cell-padding-reset"
                    data={tableData}
                    col={cols}
                    TotalBody={totalBody}
                />
            </div>
        </>
    );
};

const composeTableData = (data: any) => {
    // Group by Payer first
    const groupByPayerData = groupBy(data, "Payer");
    const reducer = (accumulator, currentValue) => {
        return accumulator + currentValue.Amount;
    };
    return Object.keys(groupByPayerData).map((e) => {
        const tempObj = { Payer: e };
        // Group by Type and calculator total
        const restData = groupBy(groupByPayerData[e], "Type");

        Object.keys(restData).forEach((el) => {
            if (PaymentTypeMapper[el]) {
                const totalAmount = restData[el].reduce(reducer, 0);
                tempObj[PaymentTypeMapper[el]] = totalAmount;
            }
        });
        return tempObj;
    });
};

const composeDataSeries = (data: any): Highcharts.SeriesOptionsType[] => {
    // Group by payer type first
    let dataSeries = [];
    const groupByPayerData = groupBy(data, "Payer");
    const reducer = (accumulator, currentValue) => {
        return currentValue.Amount + accumulator;
    };
    dataSeries = Object.keys(groupByPayerData).map((e) => {
        const tempObj = {
            type: "column",
            name: PaymentPayerMapper[e],
            pointPadding: 1,
            pointWidth: 5,
            borderWidth: 0,
            data: null,
        };

        // Handle date format
        groupByPayerData[e].forEach((e) => {
            e.DateCompleted = new Date(JsonDateParser.getFormatDate(new Date(e.DateCompleted), "yyyy-MM")).getTime();
        });

        // Compose the rest data
        const restData = groupBy(groupByPayerData[e], "DateCompleted");
        const data = Object.keys(restData).map((el) => {
            // calculate total amount
            const totalAmount = restData[el].reduce(reducer, 0);
            return { x: parseInt(el), y: totalAmount };
        });
        tempObj.data = data;
        return tempObj;
    });
    return dataSeries;
};

export const CompletedInstructions = (props: ICompletedInstructionsProps) => {
    const [data, setData] = React.useState(null);
    const [period, setPeriod] = React.useState("From inception");

    React.useEffect(() => {
        // Re-call the api and re-render the chart and table
        let date = JsonDateParser.toJsonDateNoOffset(addMonths(new Date(), -1)),
            fromInception = false;
        switch (period) {
            case "From inception":
                date = JsonDateParser.toJsonDateNoOffset(new Date());
                fromInception = true;
                break;
            case "1 month":
                date = JsonDateParser.toJsonDateNoOffset(addMonths(new Date(), -1));
                fromInception = false;
                break;
            case "1 year":
                date = JsonDateParser.toJsonDateNoOffset(addYears(new Date(), -1));
                fromInception = false;
                break;
        }
        ProductOverviewPaymentsInAction.getComletedProductPaymentsIn(
            props.subAccountHierarchyId,
            date,
            fromInception,
            (data) => {
                setData(data);
            }
        );
    }, [period, props.subAccountHierarchyId]);
    return (
        <>
            <h2 className="mb-24">Completed instructions</h2>
            <SelectEditor
                cssWrapper="control-size-180"
                options={["From inception", "1 month", "1 year"]}
                getText={(v) => {
                    return v;
                }}
                selected={period}
                onChange={(v) => {
                    setPeriod(v);
                }}
                label="Date"
            />
            {data?.length > 0 ? (
                renderChartAndTable(data, props)
            ) : (
                <div className="panel-subtle">No payments in over the date range selected</div>
            )}
        </>
    );
};
